import { create } from 'zustand';

export type ToolName =
  | 'Wwwc'
  | 'Pan'
  | 'Zoom'
  | 'Magnify'
  | 'StackScroll'
  | 'Cine'
  | 'Probe'
  | 'Length'
  | 'Angle'
  | 'CobbAngle'
  | 'RectangleROI'
  | 'EllipticalROI'
  | 'ArrowAnnotate'
  | 'Crosshairs'
  | 'ReferenceLines'
  | 'Invert'
  | 'FlipH'
  | 'FlipV'
  | 'RotateLeft'
  | 'RotateRight'
  | 'Reset'
  | 'Clear'
  | 'SyncSeries'
  | 'ToggleOverlays'
  | 'MPR'
  | '3D'
  | 'MIP';

export type LayoutType =
  | '1x1' | '1x2' | '1x3'
  | '2x1' | '2x2' | '2x3'
  | '3x1' | '3x2' | '3x3';

/**
 * Per-viewport rendering mode. Modes are now the fundamental unit — the
 * grid can freely mix a Stack in one pane, an MPR-Axial in another, and a
 * 3D-VR in a third. This is the DicomDrive v3 model: layout and mode are
 * fully decoupled.
 */
export type ViewportMode =
  | 'stack'
  | 'mpr-ax' | 'mpr-sag' | 'mpr-cor'
  | 'mip-ax' | 'mip-sag' | 'mip-cor'
  | '3d-vr';

/**
 * Global grid summary mode — retained so places that need one label
 * ("is this a volume grid?") don't have to walk every viewport. Derived
 * from the viewportModes map; setViewerMode assigns modes to every
 * viewport in a preset arrangement.
 */
export type ViewerMode = 'stack' | 'mpr' | 'mpr3d' | 'mip';

/**
 * Volume-build strategy for big series.
 *  - overview: skip every Nth slice so the whole study fits in the GPU budget
 *              (good for spatial orientation and whole-body scans)
 *  - focus:    use every slice of a user-picked contiguous window
 *              (good for localized review at full fidelity)
 */
export type VolumeBuildMode = 'overview' | 'focus';

export interface FocusWindow {
  start: number;  // inclusive
  end: number;    // exclusive
}

interface ViewerState {
  activeTool: ToolName;
  layout: LayoutType;
  /** Grid-level summary of the modes in use — 'stack' if every viewport is
   *  Stack, 'mpr' if any is orthogonal MPR (without VR), 'mip' if any is
   *  MIP, 'mpr3d' if any is 3D VR. UI that needs a coarse label reads
   *  this; UI that positions or renders per-viewport reads viewportModes. */
  viewerMode: ViewerMode;
  /** Per-viewport rendering mode. Missing entry defaults to 'stack'. */
  viewportModes: Record<number, ViewportMode>;
  /** Per-viewport slab thickness in mm (used by MIP/MinIP). Missing = 0
   *  meaning "thin slice" (proper MPR). */
  viewportSlabs: Record<number, number>;
  /** Per-viewport 3D-VR preset name (for '3d-vr' mode). */
  viewport3DPresets: Record<number, string>;
  /** Which viewport index a ModePicker action currently targets. Also
   *  drives the toolbar's "active-viewport" tools like WL preset, flip,
   *  rotate, reset. */
  activeViewportIndex: number;
  overlaysVisible: boolean;
  cineActive: boolean;
  cineFps: number;
  volumeBuildMode: VolumeBuildMode;
  /** Per-series focus window (only used when volumeBuildMode === 'focus'). */
  focusWindows: Record<number, FocusWindow>;
  // Sync flags — when true, changes to one viewport propagate to others
  syncWL: boolean;
  syncScroll: boolean;
  syncCamera: boolean;

  /** Composer basket — imageRefs (in "seriesId:imageIndex" format) that the
   *  user has queued up to send to the desktop composer app. Order-preserving:
   *  the composer fills its film cells in the order the user added them so
   *  radiologists can control layout by the order they click "Add".
   *  Deduplicates automatically; adding the same ref twice is a no-op. */
  composerBasket: string[];

  setActiveTool: (tool: ToolName) => void;
  setLayout: (layout: LayoutType) => void;
  /** Legacy global setter — assigns viewport modes based on a preset
   *  arrangement. 'stack' clears all overrides. 'mpr' → 3 orthogonal
   *  planes across the first three viewports. 'mip' → MIP-axial
   *  everywhere. 'mpr3d' → 3 planes + a 3D-VR in viewport 3. */
  setViewerMode: (mode: ViewerMode) => void;
  setViewportMode: (idx: number, mode: ViewportMode) => void;
  setViewportSlab: (idx: number, mm: number) => void;
  setViewport3DPreset: (idx: number, preset: string) => void;
  /** Reset all viewport overrides — used when the layout changes so a
   *  fresh grid starts as all Stack. */
  clearViewportOverrides: () => void;
  setActiveViewportIndex: (idx: number) => void;
  toggleOverlays: () => void;
  setCineActive: (active: boolean) => void;
  setCineFps: (fps: number) => void;
  setVolumeBuildMode: (mode: VolumeBuildMode) => void;
  setFocusWindow: (seriesId: number, win: FocusWindow) => void;
  toggleSyncWL: () => void;
  toggleSyncScroll: () => void;
  toggleSyncCamera: () => void;

  /** Add one or more imageRefs to the composer basket. Duplicates are dropped
   *  silently (based on string identity) so hitting "Add" twice on the same
   *  slice doesn't produce two entries. */
  addToComposerBasket: (refs: string | string[]) => void;
  removeFromComposerBasket: (ref: string) => void;
  clearComposerBasket: () => void;
}

/** Recompute viewerMode from the viewportModes map. */
function deriveViewerMode(viewportModes: Record<number, ViewportMode>): ViewerMode {
  const modes = Object.values(viewportModes);
  if (modes.length === 0) return 'stack';
  if (modes.some((m) => m === '3d-vr')) return 'mpr3d';
  if (modes.some((m) => m === 'mip-ax' || m === 'mip-sag' || m === 'mip-cor')) return 'mip';
  if (modes.some((m) => m === 'mpr-ax' || m === 'mpr-sag' || m === 'mpr-cor')) return 'mpr';
  return 'stack';
}

/** Preset arrangements for the legacy setViewerMode. */
function presetForMode(mode: ViewerMode): Record<number, ViewportMode> {
  switch (mode) {
    case 'stack':
      return {};
    case 'mpr':
      return { 0: 'mpr-ax', 1: 'mpr-sag', 2: 'mpr-cor' };
    case 'mip':
      return { 0: 'mip-ax', 1: 'mip-sag', 2: 'mip-cor' };
    case 'mpr3d':
      return { 0: 'mpr-ax', 1: 'mpr-sag', 2: 'mpr-cor', 3: '3d-vr' };
  }
}

export const useViewerStore = create<ViewerState>((set) => ({
  activeTool: 'Wwwc',
  layout: '1x1',
  viewerMode: 'stack',
  viewportModes: {},
  viewportSlabs: {},
  viewport3DPresets: {},
  activeViewportIndex: 0,
  overlaysVisible: true,
  cineActive: false,
  cineFps: 15,
  volumeBuildMode: 'overview',
  focusWindows: {},
  syncWL: false,
  syncScroll: false,
  syncCamera: false,
  composerBasket: [],
  setActiveTool: (activeTool) => set({ activeTool }),
  setLayout: (layout) => set({ layout }),
  setViewerMode: (viewerMode) =>
    set(() => {
      const viewportModes = presetForMode(viewerMode);
      const viewportSlabs: Record<number, number> = {};
      const viewport3DPresets: Record<number, string> = {};
      for (const [idxStr, m] of Object.entries(viewportModes)) {
        const idx = Number(idxStr);
        if (m === 'mip-ax' || m === 'mip-sag' || m === 'mip-cor') {
          viewportSlabs[idx] = 50;
        }
        if (m === '3d-vr') viewport3DPresets[idx] = 'CT-Bone';
      }
      return { viewerMode, viewportModes, viewportSlabs, viewport3DPresets };
    }),
  setViewportMode: (idx, mode) =>
    set((s) => {
      const viewportModes = { ...s.viewportModes };
      const viewportSlabs = { ...s.viewportSlabs };
      const viewport3DPresets = { ...s.viewport3DPresets };
      if (mode === 'stack') {
        delete viewportModes[idx];
        delete viewportSlabs[idx];
        delete viewport3DPresets[idx];
      } else {
        viewportModes[idx] = mode;
        // Sensible default slab for MIP; MPR stays at 0 (thin slice)
        if (mode === 'mip-ax' || mode === 'mip-sag' || mode === 'mip-cor') {
          if (!viewportSlabs[idx]) viewportSlabs[idx] = 50;
        } else if (mode !== '3d-vr') {
          // MPR — force thin slice, remove any leftover slab
          delete viewportSlabs[idx];
        }
        if (mode === '3d-vr' && !viewport3DPresets[idx]) {
          viewport3DPresets[idx] = 'CT-Bone';
        }
      }
      return {
        viewportModes,
        viewportSlabs,
        viewport3DPresets,
        viewerMode: deriveViewerMode(viewportModes),
      };
    }),
  setViewportSlab: (idx, mm) =>
    set((s) => ({ viewportSlabs: { ...s.viewportSlabs, [idx]: mm } })),
  setViewport3DPreset: (idx, preset) =>
    set((s) => ({ viewport3DPresets: { ...s.viewport3DPresets, [idx]: preset } })),
  clearViewportOverrides: () =>
    set({
      viewportModes: {},
      viewportSlabs: {},
      viewport3DPresets: {},
      viewerMode: 'stack',
    }),
  setActiveViewportIndex: (activeViewportIndex) => set({ activeViewportIndex }),
  toggleOverlays: () => set((s) => ({ overlaysVisible: !s.overlaysVisible })),
  setCineActive: (cineActive) => set({ cineActive }),
  setCineFps: (cineFps) => set({ cineFps }),
  setVolumeBuildMode: (volumeBuildMode) => set({ volumeBuildMode }),
  setFocusWindow: (seriesId, win) =>
    set((s) => ({ focusWindows: { ...s.focusWindows, [seriesId]: win } })),
  toggleSyncWL: () => set((s) => ({ syncWL: !s.syncWL })),
  toggleSyncScroll: () => set((s) => ({ syncScroll: !s.syncScroll })),
  toggleSyncCamera: () => set((s) => ({ syncCamera: !s.syncCamera })),
  addToComposerBasket: (refs) =>
    set((s) => {
      const incoming = Array.isArray(refs) ? refs : [refs];
      // Dedup preserving order: existing items keep their position;
      // new items appear in their arrival order at the end.
      const have = new Set(s.composerBasket);
      const next = [...s.composerBasket];
      for (const r of incoming) {
        if (!r) continue;
        if (have.has(r)) continue;
        have.add(r);
        next.push(r);
      }
      return { composerBasket: next };
    }),
  removeFromComposerBasket: (ref) =>
    set((s) => ({ composerBasket: s.composerBasket.filter((r) => r !== ref) })),
  clearComposerBasket: () => set({ composerBasket: [] }),
}));

/** Helper — read the effective mode for a viewport (defaults to 'stack'). */
export function getViewportMode(
  viewportModes: Record<number, ViewportMode>,
  idx: number
): ViewportMode {
  return viewportModes[idx] ?? 'stack';
}

/** Helper — is this mode volumetric (needs a Cornerstone volume actor)? */
export function isVolumeMode(mode: ViewportMode): boolean {
  return mode !== 'stack';
}

/** Helper — Cornerstone OrientationAxis for a directional MPR/MIP mode.
 *  Returns undefined for stack/3d-vr (which don't use orientations). */
export function orientationForMode(mode: ViewportMode): 'axial' | 'sagittal' | 'coronal' | undefined {
  switch (mode) {
    case 'mpr-ax':
    case 'mip-ax':
      return 'axial';
    case 'mpr-sag':
    case 'mip-sag':
      return 'sagittal';
    case 'mpr-cor':
    case 'mip-cor':
      return 'coronal';
    default:
      return undefined;
  }
}

/** Helper — human label for the ModePicker dropdown. */
export function labelForMode(mode: ViewportMode): string {
  switch (mode) {
    case 'stack':   return 'Stack';
    case 'mpr-ax':  return 'MPR — Axial';
    case 'mpr-sag': return 'MPR — Sagittal';
    case 'mpr-cor': return 'MPR — Coronal';
    case 'mip-ax':  return 'MIP — Axial';
    case 'mip-sag': return 'MIP — Sagittal';
    case 'mip-cor': return 'MIP — Coronal';
    case '3d-vr':   return '3D — Volume Render';
  }
}

/** Helper — short label for the on-viewport pill. */
export function shortLabelForMode(mode: ViewportMode): string {
  switch (mode) {
    case 'stack':   return 'Stack';
    case 'mpr-ax':  return 'Ax';
    case 'mpr-sag': return 'Sag';
    case 'mpr-cor': return 'Cor';
    case 'mip-ax':  return 'MIP · Ax';
    case 'mip-sag': return 'MIP · Sag';
    case 'mip-cor': return 'MIP · Cor';
    case '3d-vr':   return '3D VR';
  }
}
